import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, List, ListItem } from '@mui/material';
import clsx from 'clsx';
import { useBreakpoints } from 'hooks';
import { isEmpty } from 'lodash';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan } from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { Button } from 'components/_basic/Button/Button';
import { TooltipComponent } from 'components/_basic/TooltipComponent';
import { UpgradePlanTooltip } from 'components/_basic/UpgradePlanTooltip';

import { IMenuListProps } from './types';

import styles from './MenuList.module.scss';

export const MenuList: FC<IMenuListProps> = ({ clickAwayHandler, displayItems, isDocCreated, shouldDocUpdate }) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();
  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const userSubscription = useUserSubscriptionStore(getUserSubscription);

  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;
  const isUpdateOnlinePDFAvailable = businessPlan.metadata.updateOnlinePDFCount < 2 || isStandardOrLightTariff;
  return (
    <List className={styles.menuList} onClick={clickAwayHandler}>
      {displayItems.map(
        ({ title, icon, buttonTitle, isPDFUpdated, disabled = false, clickHandler, buttonClickHandler }) => (
          <ListItem
            key={title}
            className={clsx(styles.menuItem, { [styles.large]: isDocCreated })}
            onClick={(e) => {
              if (title === 'Update Online PDF' && !isUpdateOnlinePDFAvailable) {
                e.stopPropagation();
              }
            }}
          >
            <TooltipComponent
              title={<UpgradePlanTooltip />}
              isTooltipHidden={title !== 'Update Online PDF' || isUpdateOnlinePDFAvailable}
              trigger={isMobile ? 'click' : 'hover'}
              leaveDelay={isMobile ? 6500 : 200}
            >
              <Button
                className={styles.button}
                startIcon={icon}
                variant="text"
                disabled={disabled}
                onClick={() => {
                  if (title === 'Update Online PDF' && !isUpdateOnlinePDFAvailable) {
                    return;
                  } else {
                    clickHandler();
                  }
                }}
              >
                <Box
                  className={clsx({
                    [styles.buttonTextWrapper]: isPDFUpdated && shouldDocUpdate && isDocCreated,
                    [styles.updatePDFButtonText]: isPDFUpdated && shouldDocUpdate && isDocCreated,
                  })}
                  component="span"
                >
                  {title}
                </Box>
              </Button>
            </TooltipComponent>

            {!isEmpty(buttonTitle) && (
              <TooltipComponent
                title={<UpgradePlanTooltip />}
                isTooltipHidden={title !== 'Update Online PDF' || isUpdateOnlinePDFAvailable}
                trigger={isMobile ? 'click' : 'hover'}
                leaveDelay={isMobile ? 6500 : 200}
              >
                <Button onClick={buttonClickHandler} className={styles.secondaryButton} variant="text" size="small">
                  {buttonTitle}
                </Button>
              </TooltipComponent>
            )}
          </ListItem>
        ),
      )}
    </List>
  );
};
